import dynamic from "next/dynamic";

import { useCallback, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { twMerge } from "tailwind-merge";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useAuthentication from "hooks/useAuthentication";
import { useEntityWatchlists } from "hooks/useWatchlists";

const WatchlistsManageModal = dynamic(
  () => import("components/WatchlistsManageModal")
);

type AddToWatchlistButtonProps = {
  entity: any;
  style?: "full" | "small" | "card";
  className?: string;
  children?: (_options: {
    addToWatchlist: () => void;
    watchlists: any[];
    isWatching: boolean;
    isLoading: boolean;
  }) => React.ReactNode;
  activeClassName?: string;
  hideTextOnActive?: boolean;
};

const AddToWatchlistButton = ({
  entity,
  children,
  className,
  activeClassName,
  hideTextOnActive,
}: AddToWatchlistButtonProps) => {
  const { authenticated, account } = useAuthentication();
  const { toggleModal } = useSignInSignUp();

  const {
    watchlists,
    isWatching,
    isValidating: isLoading,
  } = useEntityWatchlists(entity);

  const [showManageWatchlistModal, setShowManageWatchlistModal] =
    useState(false);

  const addToWatchlist = useCallback(() => {
    if (!authenticated) {
      toggleModal("sign-in");
      return;
    }

    setShowManageWatchlistModal(true);
  }, [authenticated, toggleModal]);

  if (!authenticated || account?.canFollow) {
    return (
      <>
        {children ? (
          children({
            isWatching,
            isLoading,
            watchlists,
            addToWatchlist,
          })
        ) : (
          <OverlayTrigger
            overlay={
              isWatching ? (
                <Tooltip>
                  {entity?.prettyName} is in your Watchlist
                  {watchlists?.length > 1 ? "s" : ""}
                </Tooltip>
              ) : (
                <></>
              )
            }
          >
            <button
              className={twMerge(
                isWatching
                  ? "bg-jade hover:bg-[#419855]"
                  : "bg-smart-teal hover:bg-[#1d8988]",
                "border-none rounded-full",
                "text-white font-medium",
                "leading-[1] md:leading-normal",
                "text-[13px] md:text-[15px]",
                "px-[1.25em] py-[0.75em] md:px-[1em] md:py-[0.5em]",
                className,
                isWatching && activeClassName
              )}
              onClick={addToWatchlist}
              aria-label="add to watchlist"
            >
              <FontAwesomeIcon
                icon={isLoading ? faSpinner : isWatching ? faCheck : faPlus}
                className={twMerge(
                  isLoading ? "animate-spin" : "",
                  (!hideTextOnActive || !isWatching) && "mr-[5px]"
                )}
              />
              {(!hideTextOnActive || !isWatching) &&
                (isWatching
                  ? `${watchlists.length} Watchlist${
                      watchlists.length > 1 ? "s" : ""
                    }`
                  : "Watchlist")}
            </button>
          </OverlayTrigger>
        )}
        {showManageWatchlistModal && (
          <WatchlistsManageModal
            entity={entity}
            onClose={() => setShowManageWatchlistModal(false)}
          />
        )}
      </>
    );
  }

  return <></>;
};

export default AddToWatchlistButton;
